import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Trophy, Users, Flame, Heart, Shield, Zap, Activity,
  ThumbsUp, MessageCircle, Send, Loader2, Award
} from "lucide-react";
import { toast } from "sonner";

const COMMUNITY_BADGES = [
  { name: "Recovery Master", desc: "Kiváló regeneráció", icon: Heart, color: "#00f260" },
  { name: "Consistency Hero", desc: "Folyamatos edzés", icon: Zap, color: "#ffc107" },
  { name: "Endurance Pro", desc: "Kitartás bajnok", icon: Activity, color: "#64b5f6" },
];

export default function CommunityPage() {
  const { api, user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);

  const loadLeaderboard = useCallback(async (challengeId) => {
    try {
      const res = await api().get(`/leaderboard/${challengeId}`);
      setLeaderboard(res.data);
    } catch {}
  }, [api]);

  useEffect(() => {
    Promise.all([
      api().get("/challenges"),
      api().get("/community/feed")
    ]).then(([cRes, fRes]) => {
      setChallenges(cRes.data);
      setFeed(fRes.data);
      if (cRes.data.length > 0) {
        setSelectedChallenge(cRes.data[0]);
        loadLeaderboard(cRes.data[0].id);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api, loadLeaderboard]);

  const joinChallenge = async (challengeId) => {
    try {
      await api().post(`/challenges/${challengeId}/join`);
      toast.success("Csatlakoztál a kihíváshoz!");
      const res = await api().get("/challenges");
      setChallenges(res.data);
      loadLeaderboard(challengeId);
    } catch {
      toast.error("Hiba történt");
    }
  };

  const submitPost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      const res = await api().post("/community/feed", { text: newPost, workout_type: "", duration: "" });
      setFeed([res.data, ...feed]);
      setNewPost("");
      toast.success("Poszt megosztva!");
    } catch {
      toast.error("Hiba történt");
    } finally {
      setPosting(false);
    }
  };

  const likePost = async (postId) => {
    try {
      const res = await api().post(`/community/posts/${postId}/like`);
      setFeed(feed.map(p => {
        if (p.id === postId) {
          return { ...p, likes: p.likes + (res.data.liked ? 1 : -1), user_liked: res.data.liked };
        }
        return p;
      }));
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:pt-20" data-testid="community-page">
      <div className="flex items-center justify-between fade-in-up">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Közösség</h1>
        <Users className="text-[#00f260]" size={24} />
      </div>

      <Tabs defaultValue="challenges" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 rounded-xl p-1 w-full" data-testid="community-tabs">
          <TabsTrigger value="challenges" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#00f260] data-[state=active]:text-black">Kihívások</TabsTrigger>
          <TabsTrigger value="leaderboard" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#00f260] data-[state=active]:text-black">Ranglista</TabsTrigger>
          <TabsTrigger value="feed" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#00f260] data-[state=active]:text-black">Feed</TabsTrigger>
        </TabsList>

        {/* Challenges Tab */}
        <TabsContent value="challenges" className="space-y-4 mt-4">
          {challenges.map((ch, i) => {
            const progress = ch.participants?.length > 0
              ? Math.round((ch.participants.reduce((sum, p) => sum + p.value, 0) / (ch.target_value * ch.participants.length)) * 100)
              : 0;
            return (
              <GlassCard key={ch.id} glow className={`fade-in-up fade-in-up-${i + 1}`} testId={`challenge-card-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Flame size={18} className="text-[#00f260]" />
                  <h3 className="font-heading text-lg font-semibold text-white">{ch.name}</h3>
                </div>
                <p className="text-sm text-gray-400 mb-4">{ch.description}</p>
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Haladás</span>
                    <span className="text-[#00f260]">{progress}%</span>
                  </div>
                  <Progress value={Math.min(100, progress)} className="h-2 bg-white/5" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Users size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-400">{ch.participants?.length || 0} résztvevő</span>
                  </div>
                  {!ch.user_joined ? (
                    <button
                      data-testid={`join-challenge-${i}`}
                      onClick={() => joinChallenge(ch.id)}
                      className="neon-btn text-xs py-2 px-4"
                    >
                      Csatlakozás
                    </button>
                  ) : (
                    <Badge className="bg-[#00f260]/10 border-0 text-[#00f260] text-xs">Csatlakoztál</Badge>
                  )}
                </div>
              </GlassCard>
            );
          })}

          {/* Badges */}
          <div className="space-y-3">
            <p className="text-xs text-gray-400 uppercase tracking-wider fade-in-up">Közösségi jelvények</p>
            <div className="grid grid-cols-3 gap-3">
              {COMMUNITY_BADGES.map((badge, i) => {
                const Icon = badge.icon;
                return (
                  <GlassCard key={badge.name} className="text-center py-4 fade-in-up" testId={`community-badge-${i}`}>
                    <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: `${badge.color}15` }}>
                      <Icon size={20} style={{ color: badge.color }} />
                    </div>
                    <p className="text-xs text-white font-medium">{badge.name}</p>
                    <p className="text-[10px] text-gray-500">{badge.desc}</p>
                  </GlassCard>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-4 mt-4">
          {/* Challenge selector */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {challenges.map(ch => (
              <button
                key={ch.id}
                onClick={() => { setSelectedChallenge(ch); loadLeaderboard(ch.id); }}
                className={`px-4 py-2 rounded-xl text-xs whitespace-nowrap transition-all ${
                  selectedChallenge?.id === ch.id
                    ? "bg-[#00f260] text-black font-medium"
                    : "bg-white/5 text-gray-400 hover:bg-white/10"
                }`}
              >
                {ch.name}
              </button>
            ))}
          </div>

          {leaderboard && (
            <GlassCard testId="leaderboard-card" className="fade-in-up">
              <div className="flex items-center gap-2 mb-4">
                <Trophy size={18} className="text-[#00f260]" />
                <h3 className="font-heading text-lg font-semibold text-white">{leaderboard.challenge}</h3>
              </div>
              <div className="space-y-2">
                {leaderboard.participants?.map((p, i) => {
                  const medal = i === 0 ? "text-yellow-400" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-400" : "text-gray-500";
                  return (
                    <div
                      key={i}
                      data-testid={`leaderboard-entry-${i}`}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        i < 3 ? "bg-white/5" : ""
                      }`}
                    >
                      <span className={`font-heading text-lg font-bold w-8 ${medal}`}>{i + 1}.</span>
                      <div className="w-8 h-8 rounded-full bg-[#00f260]/10 flex items-center justify-center">
                        <span className="text-xs text-[#00f260] font-medium">{p.avatar}</span>
                      </div>
                      <span className="text-sm text-white flex-1">{p.name}</span>
                      <span className="text-sm font-medium text-[#00f260]">{p.value} {leaderboard.unit}</span>
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          )}
        </TabsContent>

        {/* Feed Tab */}
        <TabsContent value="feed" className="space-y-4 mt-4">
          {/* New Post */}
          <GlassCard testId="new-post-card" className="fade-in-up">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00f260]/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xs text-[#00f260] font-medium">{user?.name?.[0] || "?"}</span>
              </div>
              <div className="flex-1">
                <Input
                  data-testid="new-post-input"
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  placeholder="Oszd meg az edzésed..."
                  className="bg-black/20 border-white/10 text-white rounded-xl h-10 focus:border-[#00f260] focus:ring-[#00f260] placeholder:text-gray-500"
                  onKeyDown={(e) => e.key === "Enter" && submitPost()}
                />
              </div>
              <button
                data-testid="submit-post-btn"
                onClick={submitPost}
                disabled={posting || !newPost.trim()}
                className="w-10 h-10 rounded-xl bg-[#00f260] flex items-center justify-center hover:shadow-[0_0_15px_rgba(0,242,96,0.4)] transition-all disabled:opacity-40"
              >
                {posting ? <Loader2 size={16} className="animate-spin text-black" /> : <Send size={16} className="text-black" />}
              </button>
            </div>
          </GlassCard>

          {/* Feed Posts */}
          {feed.map((post, i) => (
            <GlassCard key={post.id} className={`fade-in-up fade-in-up-${i + 1}`} testId={`feed-post-${i}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#00f260]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs text-[#00f260] font-medium">{post.avatar}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white font-medium">{post.user_name}</span>
                    {post.workout_type && (
                      <Badge className="bg-white/5 border-0 text-gray-400 text-[10px]">{post.workout_type}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-white/80 mt-1">{post.text}</p>
                  {post.duration && (
                    <p className="text-xs text-gray-500 mt-1">{post.duration}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3">
                    <button
                      data-testid={`like-post-${i}`}
                      onClick={() => likePost(post.id)}
                      className={`flex items-center gap-1 text-xs transition-colors ${
                        post.user_liked ? "text-[#00f260]" : "text-gray-500 hover:text-white"
                      }`}
                    >
                      <ThumbsUp size={14} className={post.user_liked ? "fill-[#00f260]" : ""} />
                      {post.likes}
                    </button>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <MessageCircle size={14} />
                      {post.comments}
                    </span>
                  </div>
                </div>
              </div>
            </GlassCard>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
